import { useEffect, useState, type ReactNode } from 'react';
import { Backdrop, Header, type HeaderCategory } from '../../../modules/design-system';
import {
  getOrderIdFromURL,
  getViewFromURL,
  setGameIdInURL,
  setOrderIdInURL,
  setViewInURL,
} from '../../../modules/session';
import { useGamePurchase } from '../../../modules/game/LootBoxOpening';
import { RevealScreen } from '../../../modules/reveal';
import { MyPacksPage } from '../../../pages/MyPacks';
import { JustPulledPage } from '../../../pages/JustPulledPage';
import { JustPulled, useJustPulled } from '../../../modules/justPulled';
import type { ApiClient, AvailableCategory } from '../../../modules/api';
import { usePackData } from './usePackData';
import { PackLayout } from './PackLayout';
import { PurchaseOverlay } from './PurchaseOverlay';

type View = 'pack' | 'my-packs' | 'just-pulled';

export interface PackPageProps {
  client: ApiClient;
  gameId: string;
}

function toHeaderCategories(cats: AvailableCategory[]): HeaderCategory[] {
  return cats.map((c) => ({
    id: String(c.id),
    label: c.label,
    ...(c.iconSrc ? { iconSrc: c.iconSrc } : {}),
  }));
}

/**
 * Pack‑shop page container — hosts 3 mutually‑exclusive views driven by
 * URL search params so refresh / share‑link always lands on the right
 * screen:
 *
 *   ?order_id=X   → <RevealScreen>  (post‑purchase reveal, highest priority)
 *   ?view=my-packs → <MyPacksPage>  (My Packs header button)
 *   else          → <PackLayout>    (default: pack detail + buy flow)
 */
export function PackPage({ client, gameId: initialGameId }: PackPageProps) {
  const [gameId, setGameId] = useState(initialGameId);
  const [quantity, setQuantity] = useState(1);

  const [revealOrderId, setRevealOrderId] = useState<string | null>(() =>
    getOrderIdFromURL(),
  );
  const [view, setView] = useState<View>(() => {
    const fromUrl = getViewFromURL();
    if (fromUrl === 'my-packs') return 'my-packs';
    if (fromUrl === 'just-pulled') return 'just-pulled';
    return 'pack';
  });

  const {
    offer,
    categories,
    odds,
    maxForPurchaseCount,
    bgImageUrl,
    potentialHits,
    potentialHitsLoading,
  } = usePackData(client, gameId);

  // The Just Pulled feed is scoped to the active pack — both the
  // mobile/desktop strip on this page and the dedicated /just-pulled
  // sub-view share the same pack-scoped data via `poolOfferId`.
  const { items: justPulledItems, loading: justPulledLoading } = useJustPulled(client, {
    poolOfferId: gameId,
  });

  // Purchase orchestration — see `useGamePurchase` for the full
  // breakdown (wallet refresh, flow, API wrapper, quick-buy queue).
  const { buy, queueRip, retry, flow, resetFlow } = useGamePurchase({
    client,
    offerId: gameId,
    offer,
    quantity,
    setQuantity,
    onRevealReady: (orderId) => {
      setOrderIdInURL(orderId);
      setRevealOrderId(orderId);
    },
  });

  useEffect(() => {
    if (revealOrderId === null) setOrderIdInURL(null);
  }, [revealOrderId]);

  useEffect(() => {
    if (view === 'pack') {
      setViewInURL(null);
    } else {
      setViewInURL(view);
    }
  }, [view]);

  const headerCategories = toHeaderCategories(categories);
  const activeCategoryId = headerCategories.some((c) => c.id === gameId)
    ? gameId
    : undefined;

  // Pick the active screen, but DON'T early-return: PurchaseOverlay is
  // rendered at the bottom of the function as a global sibling, so its
  // loader/animation can float above any view (notably the Reveal
  // "Buy Now" CTA — without lifting it, clearing the reveal first
  // exposed the bare pack page for a frame before the overlay opened).
  let content: ReactNode;
  if (revealOrderId) {
    // Reveal has priority — even over My Packs. If an order_id is live
    // in the URL, always show the reveal screen.
    content = (
      <RevealScreen
        client={client}
        orderId={revealOrderId}
        onClose={() => setRevealOrderId(null)}
        onBuyAgain={() => {
          // Stay on Reveal: kick off the purchase directly. The global
          // PurchaseOverlay below will render on top while it runs;
          // onPurchaseCompleted swaps revealOrderId to the new order,
          // and Reveal seamlessly refreshes with the next pull.
          // `buy()` already includes the busy + status guards.
          buy();
        }}
        onShowPack={() => setRevealOrderId(null)}
      />
    );
  } else if (view === 'my-packs') {
    content = (
      <MyPacksPage
        client={client}
        onBack={() => setView('pack')}
        onOpenPack={(unopenedOffer) => {
          if (!unopenedOffer.firstUnopenedOrderId) return;
          // Don't slip a reveal in on top of an active purchase — the
          // PurchaseOverlay would land over a freshly-loaded reveal of
          // an unrelated order. No-op until the buy flow settles.
          if (flow.step !== 'idle') return;
          // Switch to this offer's pack page, drop directly into reveal.
          setGameIdInURL(String(unopenedOffer.id));
          setGameId(String(unopenedOffer.id));
          setOrderIdInURL(unopenedOffer.firstUnopenedOrderId);
          setRevealOrderId(unopenedOffer.firstUnopenedOrderId);
          setView('pack');
        }}
        onBuyAnother={(offerId) => {
          // From Drops-tab card modal — switch to that offer's pack page
          // (no auto-purchase; user lands on the pack and decides).
          const id = String(offerId);
          setGameIdInURL(id);
          setGameId(id);
          setView('pack');
        }}
      />
    );
  } else if (view === 'just-pulled') {
    content = (
      <JustPulledPage
        client={client}
        packId={gameId}
        bgImageUrl={bgImageUrl}
        onBack={() => setView('pack')}
        onRip={(offerId) => {
          // Quick-buy: switch to that offer's pack page; the hook
          // resets quantity to 1 and queues `purchase.run()` to fire
          // once the new offer finishes loading.
          const id = String(offerId);
          setGameIdInURL(id);
          setGameId(id);
          queueRip(offerId);
          setView('pack');
        }}
      />
    );
  } else {
    content = (
      <>
        {/* Backdrops live OUTSIDE the @container wrapper below — Tailwind's
            `@container` sets `container-type: inline-size`, which creates
            a containing block for `position: fixed` descendants and pulls
            them out of viewport-relative positioning (visible as a thin
            frame around the bg image). Keeping them as siblings restores
            true viewport coverage. */}
      <Backdrop bgImageUrl={bgImageUrl} />

      <div
        className="@container relative min-h-screen w-full"
        style={{ color: 'var(--color-text-on-brand)' }}
      >
      {/* MOBILE-ONLY Just Pulled — single-row panel with inline label,
          pinned to the top of the page ABOVE the REPACKZ header
          (matches Figma 226:2001). Whole strip taps to open the full
          list. Desktop variant lives in PackLayout. */}
      <div className="px-2 pt-2 @[900px]:hidden">
        <JustPulled
          compact
          items={justPulledItems}
          loading={justPulledLoading}
          onShowAll={() => setView('just-pulled')}
        />
      </div>

      <Header
        categories={headerCategories}
        {...(activeCategoryId ? { activeCategoryId } : {})}
        onCategorySelect={(id) => {
          setGameIdInURL(id);
          setGameId(id);
        }}
        onMyPacksClick={() => setView('my-packs')}
      />
      <PackLayout
        offer={offer}
        odds={odds}
        maxForPurchaseCount={maxForPurchaseCount}
        potentialHits={potentialHits}
        potentialHitsLoading={potentialHitsLoading}
        justPulledItems={justPulledItems}
        justPulledLoading={justPulledLoading}
        onShowJustPulled={() => setView('just-pulled')}
        categories={headerCategories}
        {...(activeCategoryId ? { activeCategoryId } : {})}
        onCategorySelect={(id) => {
          setGameIdInURL(id);
          setGameId(id);
        }}
        onTierSelect={(poolOfferId) => {
          const id = String(poolOfferId);
          setGameIdInURL(id);
          setGameId(id);
        }}
        quantity={quantity}
        onQuantityChange={setQuantity}
        onBuy={buy}
        unitPrice={offer?.price ?? 0}
      />
      </div>
      </>
    );
  }

  return (
    <>
      {content}
      <PurchaseOverlay state={flow} onClose={resetFlow} onRetry={retry} />
    </>
  );
}
